/* eslint-disable prettier/prettier */
import NextAuth, { AuthOptions } from 'next-auth';
import FacebookProvider from 'next-auth/providers/facebook';
import GoogleProvider from 'next-auth/providers/google';
import CredentialsProvider from 'next-auth/providers/credentials';
import { loginWithValidation, LoginRequest } from 'lib/services/login-service';
import { NextApiRequest, NextApiResponse } from 'next';
import { Favorite, getFavorites } from 'lib/services/favorites-service';
import { getClientIp } from 'lib/services/captcha-service';
import {
  FederationData,
  getCurrentUserDetails,
  getUserActivity,
  getUserStatus,
} from 'lib/services/registration-service';
import { Subscriptions } from 'lib/services/newsletter-signup-service';
import {
  buildClientProfileCookie,
  buildExpiredClientProfileCookie,
} from 'lib/utils/client-profile-cookie';
import { getSiteSettings } from 'lib/gql/getSiteSettings';
import { siteResolver } from 'lib/site-resolver';

export const SOCIAL_OAUTH_COOKIE_NAME = 'social_oauth_details';
const LEGACY_CLIENT_PROFILE_COOKIE_NAME = 'gmi.profile.id';
const NEXT_AUTH_SESSION_COOKIE_PATTERN = /^(?:__Secure-)?next-auth\.session-token(?:\.\d+)?=/i;
const NEXT_AUTH_CALLBACK_URL_COOKIE_PATTERN = /^(?:__Secure-)?next-auth\.callback-url=/i;
const AUTH_SETTINGS_CACHE_TTL_MS = 60_000;
const authSettingsCache = new Map<string, { enabled: boolean; expiresAt: number }>();

export interface SocialOAuthDetails {
  email: string;
  provider: 'google' | 'facebook';
  providerAccountId: string;
  idToken: string;
  redirectUrl: string;
  isNewUser: boolean;
  isFromSignup: boolean;
}

//  Encodes social OAuth details to Base64 for cookie storage
function encodeSocialOAuthCookie(data: SocialOAuthDetails): string {
  return Buffer.from(JSON.stringify(data)).toString('base64');
}

// Builds the social OAuth details cookie
function buildSocialOAuthCookie(
  data: SocialOAuthDetails,
  buildCookie: (name: string, value: string, attributes: string[]) => string
): string {
  const encodedCookie = encodeSocialOAuthCookie(data);
  return buildCookie(SOCIAL_OAUTH_COOKIE_NAME, encodedCookie, [
    'Path=/',
    'Secure',
    'Max-Age=300',
    'SameSite=Lax',
  ]);
}

export function buildLoginCookies(
  userId: string,
  accessToken: string,
  rememberMe: boolean
): string[] {
  const cookies = [
    `gmi.profile.oauth.access=${accessToken}; Path=/; HttpOnly; Secure`,
    `gmi.profile=${userId}; Path=/; HttpOnly; Secure`,
    `${LEGACY_CLIENT_PROFILE_COOKIE_NAME}=; Path=/; Secure; SameSite=Lax; Max-Age=0`,
  ];

  if (userId) {
    cookies.push(buildClientProfileCookie({ userId, profileType: 'User' }, rememberMe));
  }

  return cookies;
}

export function buildLogoutCookies(): string[] {
  return [
    'gmi.profile.oauth.access=; Path=/; HttpOnly; Secure; Max-Age=0',
    'gmi.profile=; Path=/; HttpOnly; Secure; Max-Age=0',
    `${SOCIAL_OAUTH_COOKIE_NAME}=; Path=/; Secure; Max-Age=0`,
    buildExpiredClientProfileCookie(),
    `${LEGACY_CLIENT_PROFILE_COOKIE_NAME}=; Path=/; Secure; SameSite=Lax; Max-Age=0`,
  ];
}

export function makeNextAuthSessionCookiesSessionOnly(
  value: number | string | readonly string[]
): number | string | readonly string[] {
  const removeExpiration = (cookie: string) =>
    NEXT_AUTH_SESSION_COOKIE_PATTERN.test(cookie)
      ? cookie.replace(/;\s*Expires=[^;]*/gi, '')
      : cookie;

  return Array.isArray(value) ? value.map(removeExpiration) : removeExpiration(String(value));
}

export function makeNextAuthCookiesForSiteSettings(
  value: number | string | readonly string[],
  hasLoginRegistrationSettings: boolean
): number | string | readonly string[] {
  const cookies = makeNextAuthSessionCookiesSessionOnly(value);
  if (hasLoginRegistrationSettings) {
    return cookies;
  }

  const keepCookie = (cookie: string) => !NEXT_AUTH_CALLBACK_URL_COOKIE_PATTERN.test(cookie);
  if (Array.isArray(cookies)) {
    return cookies.filter(keepCookie);
  }

  return keepCookie(String(cookies)) ? cookies : [];
}

async function hasLoginRegistrationSettings(req: NextApiRequest): Promise<boolean> {
  const hostName = req.headers.host?.split(':')[0] || 'localhost';
  const site = siteResolver.getByHost(hostName);
  const requestedLanguage = req.query.language;
  const language =
    (typeof requestedLanguage === 'string' && requestedLanguage) || site.language || 'en';
  const cacheKey = `${site.name}:${language}`;
  const cachedSettings = authSettingsCache.get(cacheKey);

  if (cachedSettings && cachedSettings.expiresAt > Date.now()) {
    return cachedSettings.enabled;
  }

  const siteSettings = await getSiteSettings(site.name, language);
  const enabled = Boolean(siteSettings?.loginRegistrationSettings);
  authSettingsCache.set(cacheKey, {
    enabled,
    expiresAt: Date.now() + AUTH_SETTINGS_CACHE_TTL_MS,
  });

  return enabled;
}

export function getAuthOptions(
  req: NextApiRequest,
  res: NextApiResponse,
  expiredSession: boolean
): AuthOptions {
  // If session is expired, set maxAge to 0 to expire the session
  // Otherwise, set maxAge to 1 year
  const sessionSettings = expiredSession
    ? {
        maxAge: 0,
      }
    : { maxAge: 1 * 60 * 60 * 24 * 365 };
  // Get URLs from config (generated at build time from site settings)
  const loginUrl = process.env.LOGIN_URL ?? '/user-profile/login';
  const signupUrl = process.env.SIGNUP_URL ?? '/user-profile/register';

  // Get the callbackUrl from the OAuth flow - this is where to redirect after success
  const callbackUrl = req.query?.callbackUrl as string;

  const getPathname = (url?: string) => {
    if (!url) {
      return '';
    }

    try {
      return new URL(url, process.env.PUBLIC_URL ?? 'http://localhost:3000').pathname.toLowerCase();
    } catch {
      return url.toLowerCase();
    }
  };

  const signupPath = getPathname(signupUrl);
  const callbackPath = getPathname(callbackUrl);

  // Determine if request originated from signup page
  // Check both the cookie and callbackUrl to handle OAuth redirects correctly
  const isFromSignupPage =
    req.cookies['fromSignupPage'] === 'true' ||
    (!!signupPath && !!callbackPath && callbackPath === signupPath) ||
    false;

  const authOptions: AuthOptions = {
    session: sessionSettings,
    pages: {
      signIn: loginUrl,
      signOut: '/',
      // error will redirect to login/signup page with error in query string based on origin
      error: isFromSignupPage ? signupUrl : loginUrl,
      newUser: signupUrl,
    },
    // Configure one or more authentication providers
    providers: [
      FacebookProvider({
        clientId: process.env.FACEBOOK_OAUTH_CLIENT_ID ?? '',
        clientSecret: process.env.FACEBOOK_OAUTH_SECRET ?? '',
      }),

      GoogleProvider({
        clientId: process.env.GOOGLE_OAUTH_CLIENT_ID ?? '',
        clientSecret: process.env.GOOGLE_OAUTH_SECRET ?? '',
      }),
      CredentialsProvider({
        name: 'Credentials',
        credentials: {
          username: { label: 'Username', type: 'email', placeholder: 'jsmith' },
          password: { label: 'Password', type: 'password' },
          captchaToken: { label: 'Captcha Token', type: 'hidden' },
          rememberMe: { label: 'Remember Me', type: 'checkbox' },
        },
        authorize: async (_credentials, _req) => {
          // We will be overriding this in the signIn callback but we are
          // required to return a user object
          return { id: '' };
        },
      }),
      // Social Connect Provider - for linking social account to existing user
      CredentialsProvider({
        id: 'social_connect',
        name: 'Social Connect',
        credentials: {
          username: { label: 'Username', type: 'email' },
          password: { label: 'Password', type: 'password' },
          provider: { label: 'OAuth Provider', type: 'hidden' },
          providerUserId: { label: 'Provider User ID', type: 'hidden' },
          providerUserToken: { label: 'Provider User Token', type: 'hidden' },
        },
        authorize: async (_credentials, _req) => {
          // We will be overriding this in the signIn callback
          return { id: '' };
        },
      }),
      // OAuth Token Provider - for logging in with federation token after registration
      CredentialsProvider({
        id: 'oauth_token',
        name: 'OAuth Token Login',
        credentials: {
          provider: { label: 'OAuth Provider', type: 'hidden' },
          userToken: { label: 'User Token', type: 'hidden' },
        },
        authorize: async (_credentials, _req) => {
          // We will be overriding this in the signIn callback
          return { id: '' };
        },
      }),
    ],
    callbacks: {
      signIn: async ({ user, account, credentials }) => {
        let loginRequest: LoginRequest;

        // Check if this is a social_connect request (credentials provider with id 'social_connect')
        const isSocialConnect =
          account?.type === 'credentials' && account?.provider === 'social_connect';
        // Check if this is an oauth_token request (credentials provider with id 'oauth_token')
        const isOAuthToken = account?.type === 'credentials' && account?.provider === 'oauth_token';

        switch (account?.type) {
          case 'credentials':
            if (!credentials) {
              return false;
            }

            // Handle oauth_token flow (login with federation token after registration)
            if (isOAuthToken) {
              loginRequest = {
                type: 'oauth',
                oauth: {
                  provider: credentials.provider as string,
                  userToken: credentials.userToken as string,
                },
              };
            } else if (isSocialConnect) {
              loginRequest = {
                type: 'social_connect',
                credentials: {
                  username: credentials.username as string,
                  password: credentials.password as string,
                },
                federation: {
                  provider: credentials.provider as string,
                  providerUserToken: credentials.providerUserToken as string,
                },
                skipCaptcha: true,
              };
            } else {
              loginRequest = {
                type: 'credentials',
                credentials: {
                  username: credentials.username as string,
                  password: credentials.password as string,
                },
                captchaToken: credentials.captchaToken as string,
                skipCaptcha:
                  process.env.VERCEL_ENV === 'preview' ||
                  process.env.PUBLIC_URL === 'http://localhost:3000',
              };
            }
            break;
          case 'oauth':
            loginRequest = {
              type: 'oauth',
              oauth: {
                provider: account.provider,
                userToken: (account.id_token || account.access_token) as string,
              },
            };
            break;
          default:
            console.error('unsupport login type', account?.type);
            return false;
        }

        const loginResponse = await loginWithValidation(loginRequest, getClientIp(req));

        const buildCookie = (name: string, value: string, attributes: string[]) => {
          const allAttributes = attributes.filter(Boolean).join('; ');
          return `${name}=${value}${allAttributes ? `; ${allAttributes}` : ''}`;
        };

        if (loginResponse.result === 'success') {
          user.id = loginResponse.userProfileDetails?.UserId ?? '';
          user.name =
            `${loginResponse.userProfileDetails?.FirstName} ${loginResponse.userProfileDetails?.LastName}`.trim();
          user.email = loginResponse.userProfileDetails?.Email;
          user.image = '';
          account.userId = loginResponse.userProfileDetails?.UserId;

          const favorites = await getFavorites(loginResponse.accessToken || '');
          if (favorites.error) {
            console.error('error fetching favorites', favorites.error_description);
            return false;
          }
          account.favorites = favorites.Favorites as Favorite[];
          account.accessToken = loginResponse.accessToken;
          account.hasPassword = loginResponse.userProfileDetails?.HasPassword === true;

          account.subscriptions = loginResponse.userProfileDetails?.Subscriptions;
          const rememberMe = credentials?.rememberMe as string | boolean | undefined;
          const cookies = buildLoginCookies(
            loginResponse.userProfileDetails?.UserId ?? '',
            loginResponse.accessToken ?? '',
            rememberMe === true || rememberMe === 'true'
          );

          res.setHeader('Set-Cookie', cookies);
          return true;
        } else {
          const isOAuthProvider =
            account?.provider === 'google' || account?.provider === 'facebook';

          if (isOAuthProvider) {
            const userEmail = user.email || '';
            const provider = account.provider as 'google' | 'facebook';
            const providerAccountId = account.providerAccountId || '';
            const idToken = account.id_token || account.access_token || '';

            // Check if existing user needs social connect
            let isExistingUserWithPassword = false;
            const userStatus = await getUserStatus(userEmail);
            console.log('userStatus', userStatus);
            if (userStatus?.Status === 'User') {
              const currentUserDetails = await getCurrentUserDetails(userStatus.UserId);
              isExistingUserWithPassword =
                currentUserDetails?.HasPassword === true &&
                !(currentUserDetails?.Federation ?? []).some(
                  (f: FederationData) => f.Provider === provider
                );
            } else {
              const userActivity = await getUserActivity(userEmail);
              console.log('userActivity', userActivity);
              if (userActivity?.UserId) {
                const currentUserDetails = await getCurrentUserDetails(userActivity.UserId);
                isExistingUserWithPassword =
                  currentUserDetails?.HasPassword === true &&
                  !(currentUserDetails?.Federation ?? []).some(
                    (f: FederationData) => f.Provider === provider
                  );
              }
            }

            // If user doesn't exist and they came from login page, show error on login
            // They should use signup page to create an account
            if (!isExistingUserWithPassword && !isFromSignupPage) {
              console.error('loginResponse error', loginResponse);
              throw new Error(loginResponse.result);
            }

            // Get actual origin URL from callbackUrl (set by NextAuth during OAuth flow)
            // Fallback to config URLs if callbackUrl is missing or invalid
            let originPath = isFromSignupPage ? signupUrl : loginUrl;
            let successRedirectUrl = '/';

            try {
              if (callbackUrl) {
                const callbackUrlObj = new URL(callbackUrl);
                // Use the actual origin path with query params for redirect back to connect flow
                originPath = callbackUrlObj.pathname + callbackUrlObj.search;

                if (isFromSignupPage) {
                  // Signup flow: append register=true to existing params
                  callbackUrlObj.searchParams.set('registered', 'true');
                  successRedirectUrl = callbackUrlObj.pathname + callbackUrlObj.search;
                } else {
                  // Login flow: use returnUrl for success redirect
                  const returnUrl = callbackUrlObj.searchParams.get('returnUrl');
                  successRedirectUrl = returnUrl || '/';
                }
              } else {
                // Fallback when callbackUrl is missing
                successRedirectUrl = isFromSignupPage ? `${signupUrl}?registered=true` : '/';
              }
            } catch {
              // Fallback if callbackUrl parsing fails
              successRedirectUrl = isFromSignupPage ? `${signupUrl}?registered=true` : '/';
            }

            const socialOAuthData: SocialOAuthDetails = {
              email: userEmail,
              provider: provider,
              providerAccountId: providerAccountId,
              idToken: idToken,
              redirectUrl: successRedirectUrl,
              isNewUser: !isExistingUserWithPassword,
              isFromSignup: isFromSignupPage,
            };

            const socialOAuthCookie = buildSocialOAuthCookie(socialOAuthData, buildCookie);
            res.setHeader('Set-Cookie', socialOAuthCookie);

            const redirectPath = isExistingUserWithPassword
              ? originPath
              : isFromSignupPage
                ? originPath
                : signupUrl;
            res.redirect(redirectPath);

            return true;
          }
          console.error('loginResponse error', loginResponse);
          throw new Error(loginResponse.result);
        }
      },

      jwt: async ({ token, account, trigger, session }) => {
        if (trigger === 'signIn' && account) {
          return {
            ...token,
            userId: account.userId,
            // favorites are NOT stored in the JWT token to prevent the cookie from growing
            // too large (494 REQUEST_HEADER_TOO_LARGE) when a user has many saved items.
            // The session callback always re-fetches favorites fresh from the API.
            accessToken: account.accessToken,
            hasPassword: account.hasPassword,
            subscriptions: account.subscriptions,
            // userProfileDetails: account.userProfileDetails,
          };
        }
        // Support client-side updates via useSession().update({ user: { accessToken, subscriptions } })
        if (trigger === 'update') {
          const updatedToken = { ...token };
          // favorites are intentionally excluded — see signIn comment above
          if (session?.user?.accessToken) {
            updatedToken.accessToken = session.user.accessToken;
          }
          if (session?.user?.hasPassword !== undefined) {
            updatedToken.hasPassword = session.user.hasPassword;
          }
          if (session?.user?.subscriptions) {
            updatedToken.subscriptions = session.user.subscriptions as Subscriptions[];
          }
          return updatedToken;
        }
        return token;
      },

      session: async ({ session, token }) => {
        if (session.user) {
          session.user.id = token.userId as string;
          session.user.accessToken = token.accessToken as string;
          session.user.hasPassword = token.hasPassword as boolean | undefined;
          session.user.subscriptions = token.subscriptions as Subscriptions[];
          // session.userProfileDetails = token.userProfileDetails;

          const accessToken = token.accessToken as string;
          if (accessToken) {
            try {
              const favorites = await getFavorites(accessToken);
              if (!favorites.error && favorites.Favorites) {
                session.user.favorites = favorites.Favorites as Favorite[];
              } else {
                // favorites are not cached in the JWT token (to keep cookie size small),
                // so fall back to an empty array when the API call fails.
                session.user.favorites = [];
                if (favorites.error) {
                  console.error(
                    'Error fetching favorites in session callback:',
                    favorites.error_description
                  );
                }
              }

              if (session?.user?.id) {
                const currentUserDetails = await getCurrentUserDetails(session?.user?.id as string);
                if (!currentUserDetails?.error && currentUserDetails?.Subscriptions) {
                  session.user.subscriptions = currentUserDetails.Subscriptions as Subscriptions[];
                } else {
                  // Fallback to cached subscriptions if fetch fails
                  session.user.subscriptions = (token.subscriptions as Subscriptions[]) || [];
                }
              }
            } catch (error) {
              // favorites are not cached in the JWT token (to keep cookie size small)
              session.user.favorites = [];
              console.error('Error fetching favorites in session callback:', error);
            }
          } else {
            // No access token
            session.user.favorites = [];
          }
        }
        return session;
      },
    },
    events: {
      signOut: async () => {
        res.setHeader('Set-Cookie', buildLogoutCookies());
      },
      createUser: async (data) => {
        console.log('createUser', data);
      },
    },
  };
  return authOptions;
}

export default async function auth(req: NextApiRequest, res: NextApiResponse) {
  // Full authentication requires the session-only access cookie as well as NextAuth's JWT.
  const accessToken = req.cookies['gmi.profile.oauth.access'] ?? '';

  // Only expire session if we are on the session endpoint and we don't have an access token
  // We need the url check otherwise initial login will expire the session since the cookie is set after the login.
  const expiredSession = req.url?.includes('/api/auth/session') === true && !accessToken;
  let allowCallbackUrlCookie = false;

  try {
    allowCallbackUrlCookie = await hasLoginRegistrationSettings(req);
  } catch (error) {
    console.error('Could not load Sitecore login-registration settings:', error);
  }

  const originalSetHeader = res.setHeader.bind(res);
  res.setHeader = (name, value) => {
    if (name.toLowerCase() === 'set-cookie') {
      return originalSetHeader(
        name,
        makeNextAuthCookiesForSiteSettings(value, allowCallbackUrlCookie)
      );
    }

    return originalSetHeader(name, value);
  };

  return await NextAuth(req, res, getAuthOptions(req, res, expiredSession));
}
